import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'
import TranferSuccessImage from '@/assets/images/transfer-success.png'
import { TranscriptReadingFeedback } from '@/components/feedback'
import { IzzyAnalyze } from '@/components/izzy/analyze'
import { FinalStepLoading } from '@/components/layout/final-step-loading'
import { StudentStepLoadingLayout } from '@/components/layout/student-step-loading-layout'
import { CoursesEquivalences } from '@/components/tables/courses-quivalences'
import type { FileUploadStatus } from '@/types/file-upload/file-upload-status'
import type {
  StudentAutomaticCoursesFormMethods,
  StudentRequestedCoursesFormMethods
} from '@/types/forms/student-requested-courses-form-methods'
import type { Request } from '@/types/requests/request'
import { transferIzzyVariants } from './config/transfer-izzy-variants'
import { useCheckTheEquivalences } from './hooks/use-check-the-equivalences'

interface CheckTheEquivalencesProps {
  formMethods: StudentRequestedCoursesFormMethods
  formMethodsAI: StudentAutomaticCoursesFormMethods
  isPendingEquivalenceGenerate: boolean
  isPendingSubmit?: boolean
  fileStatus?: FileUploadStatus
  isSuccessSubmit?: boolean
  request?: Request
  hasAutomaticEquivalences?: boolean
}

export const CheckTheEquivalences = ({
  formMethods,
  formMethodsAI,
  isPendingEquivalenceGenerate,
  isPendingSubmit,
  isSuccessSubmit,
  fileStatus,
  request,
  hasAutomaticEquivalences
}: CheckTheEquivalencesProps) => {
  const [transcriptStatus, setTranscriptStatus] =
    useState<keyof typeof transferIzzyVariants>('success')

  const {
    states: { isPageLoading, studentDesiredCourses }
  } = useCheckTheEquivalences(request)

  const izzyConfig = transferIzzyVariants[transcriptStatus]

  function handleFileStatus(fileStatus: FileUploadStatus) {
    if (fileStatus === 'warning') {
      setTranscriptStatus('error')
      return
    }

    if (!hasAutomaticEquivalences && fileStatus === 'success') {
      setTranscriptStatus('withoutEquivalence')
      return
    }

    if (fileStatus === 'success' && hasAutomaticEquivalences) {
      setTranscriptStatus('success')
      return
    }
  }

  function renderPage() {
    return (
      <div className="w-full relative">
        {izzyConfig.izzyGif}
        <TranscriptReadingFeedback variant={transcriptStatus} />

        <CoursesEquivalences
          curriculumCourses={studentDesiredCourses}
          request={request}
          formMethods={formMethods}
          formMethodsIA={formMethodsAI}
          previousCourses={request?.previousEducation?.previousEducationCourses}
          showDisciplineButton={true}
          showCurriculumButton={false}
          showStatusColumn={false}
          variant={'student'}
        />
      </div>
    )
  }

  function renderSubmitLoading() {
    return (
      <AnimatePresence mode="wait">
        {isSuccessSubmit && (
          <motion.div
            key="success"
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 200, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="flex flex-col items-center gap-5"
          >
            <img src={TranferSuccessImage} alt="Imagem de sucesso" />
            <p className="text-[var(--brand-green)] font-bold text-2xl">
              Solicitação de Análise Enviada
            </p>
          </motion.div>
        )}

        {isPendingSubmit && (
          <motion.div
            key="loading"
            initial={{ x: -200, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 200, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full"
          >
            <FinalStepLoading loadingLabel="Enviando Solicitação de Análise" />
          </motion.div>
        )}
      </AnimatePresence>
    )
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (fileStatus) {
      handleFileStatus(fileStatus)
    }
  }, [fileStatus, hasAutomaticEquivalences])

  return (
    <StudentStepLoadingLayout
      isLoading={isPendingEquivalenceGenerate || isPageLoading}
      loadingLabel={[
        'Agora estou comparando suas matérias com o nosso currículo. Isso leva só alguns segundos...',
        'Só mais um pouco...'
      ]}
      className="min-h-110 mt-12"
      loadingImage={<IzzyAnalyze className="w-60 z-50" />}
    >
      <div>
        {!isPendingSubmit && !isSuccessSubmit && renderPage()}
        {(isPendingSubmit || isSuccessSubmit) && renderSubmitLoading()}
      </div>
    </StudentStepLoadingLayout>
  )
}
